import { School } from '@mui/icons-material';
import { useCommunityContext } from 'providers';
import { useMemo } from 'react';

import {
  CommunityProfileFragment,
  CurrentCommunityFragment,
  SchoolConfigurationFragment,
} from 'shared/graphql';
import { generateCommunityRoute } from 'shared/routes';

import { AuthorAvatar, AuthorAvatarProps } from './AuthorAvatar';

export interface CommunityAvatarProps extends Omit<AuthorAvatarProps, 'name' | 'source' | 'route'> {
  /** @deprecated Use `community` instead. */
  school?: SchoolConfigurationFragment;

  /**
   * The community object to display the avatar for. If not provided, the current community configuration will be used.
   */
  community?:
    | SchoolConfigurationFragment
    | CurrentCommunityFragment
    | CommunityProfileFragment
    | null;
}

/**
 * Renders a community avatar component that displays an avatar for a school.
 * The component uses the provided school data or falls back to the current community if no `school` is explicitly passed.
 * Uses the `CommunityProvider`, which is expected to be provided by the parent component.
 *
 * @param {CommunityAvatarProps} props - The props for the CommunityAvatar component.
 * @param {School | undefined} props.school - The school object to display the avatar for. If not provided, the current school configuration will be used.
 * @param {Object} props.props - Additional props to be passed to the AuthorAvatar component.
 */
export function CommunityAvatar({
  school: givenSchool,
  community: givenCommunity = givenSchool,
  size = 40,
  ...props
}: CommunityAvatarProps) {
  const { selectedCommunity: currentCommunity } = useCommunityContext();

  const community = givenCommunity ?? currentCommunity;

  const name = community?.name;
  const source = community?.images?.logo?.downloadUrl ?? undefined;

  const route = useMemo(() => {
    return generateCommunityRoute(community?.id);
  }, [community]);

  return (
    <AuthorAvatar {...props} size={size} source={source} name={name} route={route}>
      <School fontSize="inherit" />
    </AuthorAvatar>
  );
}

/** @deprecated Use `CommunityAvatar` instead. */
export const SchoolAvatar = CommunityAvatar;
